import type {DefaultError} from "@tanstack/react-query";
import {useMutation} from "@tanstack/react-query";
import {isAxiosError} from "axios";
import {queryClient} from "~/query/queryClient.ts";
import {queryHttp} from "~/query/queryHttp.ts";
import type {ContractDetailApi} from "./useContractDetailQuery.ts";
import {contractDetailUrl} from "./useContractDetailQuery.ts";
import {contractsUrl} from "./useContractExplorerQueries.ts";

/** One editable item row; `null` clears a value. The converted columns
 *  (`item_spend`, clean currency, rate) are computed server-side on save. */
export type ReviewItemDraft = {
    item_name: string;
    item_description: string;
    item_quantity: number | null;
    item_unit: string;
    item_unit_price: number | null;
    item_price_currency: string;
    item_spend_net: number | null;
    item_spend_gross: number | null;
    payment_frequency: string;
    pricing_model: string;
};

export type ReviewTermDraft = {
    term_type: string;
    net_days_value: string;
    payment_method: string;
    term_details: string;
};

export type ReviewContractVariables = {
    bagId: number;
    contractId: number;
    /** The version the edit was based on; a mismatch is a 409 conflict. */
    expectedVersionNo: number;
    /** Only the header fields being changed. */
    header: Record<string, string | number | null>;
    /** Full replacement sets; omit to keep the current rows untouched. */
    items?: ReviewItemDraft[];
    paymentTerms?: ReviewTermDraft[];
};

/** True when the save failed because someone saved a newer version first. */
export const isVersionConflict = (error: unknown): boolean =>
    isAxiosError(error) && error.response?.status === 409;

/**
 * `POST /api/data/{bagId}/contracts/{id}/review/` — appends a user version.
 *
 * The response is the fresh detail payload, so the detail cache is replaced in
 * place; the list windows are invalidated because the row's title, amount and
 * counts may all have changed.
 */
export function useReviewContractMutation() {
    return useMutation<ContractDetailApi, DefaultError, ReviewContractVariables>({
        mutationFn: async ({bagId, contractId, expectedVersionNo, header, items, paymentTerms}) => {
            const {data} = await queryHttp.post<ContractDetailApi>(
                `${contractDetailUrl(bagId, contractId)}review/`,
                {
                    expected_version_no: expectedVersionNo,
                    header,
                    ...items !== undefined && {items},
                    ...paymentTerms !== undefined && {payment_terms: paymentTerms},
                },
            );
            return data;
        },
        onSuccess: (data, {bagId, contractId}) => {
            queryClient.setQueryData([contractDetailUrl(bagId, contractId)], data);
            void queryClient.invalidateQueries({
                predicate: query => String(query.queryKey[0]).startsWith(contractsUrl(bagId)) &&
                    query.queryKey[0] !== contractDetailUrl(bagId, contractId),
            });
        },
    });
}
